import { useState, useEffect } from 'react';
import api from '../utils/api';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CheckCircle, XCircle, Users, TrendUp } from '@phosphor-icons/react';

const AttendanceReport = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const { activeBranch } = useBranch();

  useEffect(() => {
    if (activeBranch) {
      fetchReport();
    }
  }, [selectedDate, activeBranch]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/attendance/report`, {
        params: { 
          date: selectedDate,
          branch_id: activeBranch?.branch_id
        }
      });
      setReport(response.data);
    } catch (error) {
      console.error('Failed to load attendance report:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'New': 'bg-blue-100 text-blue-800 border border-blue-300',
      '10 Days': 'bg-green-100 text-green-800 border border-green-300',
      '30 Days': 'bg-yellow-100 text-yellow-800 border border-yellow-300',
      '60 Days': 'bg-orange-100 text-orange-800 border border-orange-300',
      '90 Days': 'bg-red-100 text-red-800 border border-red-300',
      '120 Days': 'bg-purple-100 text-purple-800 border border-purple-300',
      'Buyers': 'bg-emerald-100 text-emerald-800 border border-emerald-300',
      'Challenge Therapy': 'bg-pink-100 text-pink-800 border border-pink-300',
      // Legacy support
      'New Customer': 'bg-blue-100 text-blue-800 border border-blue-300',
      '10 Day Customer': 'bg-green-100 text-green-800 border border-green-300',
      '30 Days Customer': 'bg-yellow-100 text-yellow-800 border border-yellow-300',
      '60 Days Customer': 'bg-orange-100 text-orange-800 border border-orange-300',
      '90 Days Customer': 'bg-red-100 text-red-800 border border-red-300',
      '120+ Days Customer': 'bg-purple-100 text-purple-800 border border-purple-300'
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border border-gray-300';
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#2C4C3B] border-r-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="attendance-report-page">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-[#2C4C3B] mb-2">Attendance Report</h1>
          <p className="text-sm sm:text-base text-[#1C1C1A]/60">Present and absent list for scheduled batches</p>
        </div>

        <div className="mb-6">
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A] mb-2 block">Select Date</Label>
          <Input
            data-testid="date-filter-input"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-64"
          />
        </div>

        {report && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card className="p-6 bg-white border border-[#E5E5DF] rounded-lg" data-testid="expected-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]/60 mb-1">Expected</p>
                    <p className="text-3xl font-semibold tracking-tight text-[#2C4C3B]">{report.summary.total_expected}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-[#2C4C3B]/10">
                    <Users size={24} weight="duotone" className="text-[#2C4C3B]" />
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-white border border-[#E5E5DF] rounded-lg" data-testid="present-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]/60 mb-1">Present</p>
                    <p className="text-3xl font-semibold tracking-tight text-green-600">{report.summary.total_present}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-green-100">
                    <CheckCircle size={24} weight="duotone" className="text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-white border border-[#E5E5DF] rounded-lg" data-testid="absent-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]/60 mb-1">Absent</p>
                    <p className="text-3xl font-semibold tracking-tight text-red-600">{report.summary.total_absent}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-red-100">
                    <XCircle size={24} weight="duotone" className="text-red-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-white border border-[#E5E5DF] rounded-lg" data-testid="rate-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]/60 mb-1">Rate</p>
                    <p className="text-3xl font-semibold tracking-tight text-[#C05C42]">{report.summary.attendance_rate}%</p>
                  </div>
                  <div className="p-3 rounded-lg bg-[#C05C42]/10">
                    <TrendUp size={24} weight="duotone" className="text-[#C05C42]" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Category-wise Breakdown Table */}
            {report.category_breakdown && (
              <Card className="bg-white border border-[#E5E5DF] rounded-lg overflow-hidden mb-8">
                <div className="p-6 bg-[#E2E8E4] border-b border-[#E5E5DF]">
                  <h2 className="text-xl font-semibold text-[#2C4C3B]">Category-wise Attendance</h2>
                  <p className="text-sm text-[#1C1C1A]/60 mt-1">Present and absent breakdown by customer category</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F5F5F0]">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Category</th>
                        <th className="px-6 py-4 text-center text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Present</th>
                        <th className="px-6 py-4 text-center text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Absent</th>
                        <th className="px-6 py-4 text-center text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5DF]">
                      {Object.entries(report.category_breakdown).map(([category, counts]) => (
                        counts.total > 0 && (
                          <tr key={category} className="hover:bg-[#F9F9F8] transition-colors">
                            <td className="px-6 py-4">
                              <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${getCategoryColor(category)}`}>
                                {category}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <CheckCircle size={18} weight="fill" className="text-green-600" />
                                <span className="text-lg font-semibold text-green-700">{counts.present}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <XCircle size={18} weight="fill" className="text-red-600" />
                                <span className="text-lg font-semibold text-red-700">{counts.absent}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="text-lg font-semibold text-[#2C4C3B]">{counts.total}</span>
                            </td>
                          </tr>
                        )
                      ))}
                      <tr className="bg-[#E2E8E4] font-semibold">
                        <td className="px-6 py-4 text-sm uppercase tracking-[0.2em] text-[#1C1C1A]">TOTAL</td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <CheckCircle size={20} weight="fill" className="text-green-600" />
                            <span className="text-xl font-bold text-green-700">
                              {Object.values(report.category_breakdown).reduce((sum, counts) => sum + counts.present, 0)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <XCircle size={20} weight="fill" className="text-red-600" />
                            <span className="text-xl font-bold text-red-700">
                              {Object.values(report.category_breakdown).reduce((sum, counts) => sum + counts.absent, 0)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-xl font-bold text-[#2C4C3B]">
                            {Object.values(report.category_breakdown).reduce((sum, counts) => sum + counts.total, 0)}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            <Tabs defaultValue="present" className="w-full">
              <TabsList className="grid w-full max-w-md grid-cols-2">
                <TabsTrigger value="present">Present ({report.summary.total_present})</TabsTrigger>
                <TabsTrigger value="absent">Absent ({report.summary.total_absent})</TabsTrigger>
              </TabsList>

              <TabsContent value="present" className="mt-6">
                <Card className="bg-white border border-[#E5E5DF] rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="present-table">
                      <thead className="bg-[#E2E8E4]">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Reg No</th>
                          <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Name</th>
                          <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Phone</th>
                          <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Category</th>
                          <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Batch Time</th>
                          <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Check In</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5E5DF]">
                        {report.present.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="px-6 py-8 text-center text-[#1C1C1A]/60">
                              No one present yet
                            </td>
                          </tr>
                        ) : (
                          report.present.map((customer) => (
                            <tr key={customer.customer_id} className="hover:bg-[#F9F9F8] transition-colors">
                              <td className="px-6 py-4 font-mono text-sm text-[#2C4C3B]">{customer.reg_no}</td>
                              <td className="px-6 py-4 font-medium text-[#2C4C3B]">{customer.name}</td>
                              <td className="px-6 py-4 text-[#1C1C1A]/70">{customer.phone_number}</td>
                              <td className="px-6 py-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(customer.customer_category)}`}>
                                  {customer.customer_category}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-[#1C1C1A]/70">{customer.batch_time}</td>
                              <td className="px-6 py-4 text-sm text-[#1C1C1A]/70">
                                {new Date(customer.checked_in_at).toLocaleTimeString()}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="absent" className="mt-6">
                <Card className="bg-white border border-[#E5E5DF] rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="absent-table">
                      <thead className="bg-[#E2E8E4]">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Reg No</th>
                          <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Name</th>
                          <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Phone</th>
                          <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Category</th>
                          <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Expected Batch</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5E5DF]">
                        {report.absent.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-6 py-8 text-center text-green-600">
                              Perfect attendance! No absences today.
                            </td>
                          </tr>
                        ) : (
                          report.absent.map((customer) => (
                            <tr key={customer.customer_id} className="hover:bg-[#F9F9F8] transition-colors">
                              <td className="px-6 py-4 font-mono text-sm text-[#2C4C3B]">{customer.reg_no}</td>
                              <td className="px-6 py-4 font-medium text-[#2C4C3B]">{customer.name}</td>
                              <td className="px-6 py-4 text-[#1C1C1A]/70">{customer.phone_number}</td>
                              <td className="px-6 py-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(customer.customer_category)}`}>
                                  {customer.customer_category}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-[#1C1C1A]/70">{customer.expected_batch_time}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </Layout>
  );
};

export default AttendanceReport;